import React, { useState } from "react"
import { useParams } from "react-router-dom"
import { Flex, Text } from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import Menu, { MenuItem } from "components/dialogs/Menu"
import {
  EventGroup, GuestFilter, GuestFilterType, ModalName, Segment, AccountUser, EventCounts,
  SegmentSize,
} from "sharedTypes"
import useModal from "services/useModal"
import { useSegmentSizes } from "queries/events"
import { asNumber } from "utilities/strings"
import { withSectionBoundary } from "components/ErrorBoundary"
import FilterGroup from "./FilterGroup"
import FilterIcon, { TABULAR_NUMS_SX } from "./FilterIcon"
import CheckInAlert from "../../SelectedGuest/Show/CheckInAlert"

type Props = {
  counts: EventCounts;
  eventGroups: EventGroup[];
  segmentsEnabled: boolean;
  segments: Segment[];
  selected: {segmentId?: string; groupId?: string | number;};
  onChange: (filter) => void;
  canEditGuests: boolean;
  teamMembers: AccountUser[];
  // True when the active identity (custom segment OR default group preset)
  // has been modified via the chip row. Appends "*" after the title so the
  // user sees at a glance that the view is out of sync with the picked
  // identity.
  isViewDirty?: boolean;
  // True when chips exist but no identity is picked (or the picked identity
  // has no chip representation, e.g. Unassigned). Title shows
  // "Unsaved Segment" instead of the picked identity's label — otherwise
  // we'd lie with "All Guests" while filters are clearly applied.
  isUnsavedView?: boolean;
}

const Filters = ({
  counts, eventGroups, segmentsEnabled, segments, onChange, selected, canEditGuests, teamMembers,
  isViewDirty, isUnsavedView,
}: Props) => {
  const showModal = useModal()
  const { eventId } = useParams<{ eventId: string }>()
  const segmentSizes = useSegmentSizes(eventId)
  const [isOpen, setIsOpen] = useState(false)
  const [editCheckInAlert, setEditCheckInAlert] = useState<EventGroup | null>(null)

  const selectFilter = (filter) => {
    onChange(filter)
  }

  const filterProperties = (filter) => (
    {
      ...filter,
      key: filter.groupId || filter.segmentId,
      onSelect: () => selectFilter(filter),
      selected: isSelected(filter, selected),
      onEditCheckinAlert: canEditGuests ? () => setEditCheckInAlert(filter.eventGroup) : () => {
      },
    })

  const filters = allFilters(
    eventGroups, counts, segmentsEnabled ? segments : [], segmentSizes,
  )

  const selectedFilter = findSelectedFilter(filters, selected)

  const generateFilters = (type: GuestFilterType) => filters
    .filter((filter) => filter.type === type)
    .map((filter) => filterProperties(filter))

  const closeMenu = () => setIsOpen(false)

  const allFilter = generateFilters(GuestFilterType.All)[0]

  const confirmationStatusIds = [
    "confirmed", "unconfirmed", "waitlisted", "declined",
  ]
  const defaultSegmentFilters = generateFilters(GuestFilterType.DefaultSegment)
  const confirmationFilters = defaultSegmentFilters
    .filter((f) => confirmationStatusIds.includes(f.groupId))
  const segmentFilters = defaultSegmentFilters
    .filter((f) => !confirmationStatusIds.includes(f.groupId))

  const menuItems = [
    <MenuItem
      key="all"
      onClick={allFilter.onSelect}
      fontSize="md"
      bg={allFilter.selected ? "blue.50" : undefined}
      px={4}
      py={2.5}
    >
      <Flex align="center" justify="space-between" w="full">
        <Flex align="center" overflow="hidden" mr={4}>
          <Icon
            icon={allFilter.icon || "dot"}
            size={["dot", "circle"].includes(allFilter.icon) ? 3 : 4}
            mr={2}
            flexShrink={0}
            color={allFilter.iconColor}
          />
          <Text isTruncated>{allFilter.label}</Text>
        </Flex>
        <Text as="span" flexShrink={0} sx={TABULAR_NUMS_SX}>
          {asNumber(allFilter.count)}
        </Text>
      </Flex>
    </MenuItem>,
    <FilterGroup
      key="confirmation-status"
      label="Confirmation Status"
      filters={confirmationFilters}
      onCloseMenu={closeMenu}
    />,
    <FilterGroup
      key="segments"
      label="Segments"
      filters={segmentFilters}
      onCloseMenu={closeMenu}
    />,
    <FilterGroup
      key="custom-segments"
      label="Custom Segments"
      filters={generateFilters(GuestFilterType.CustomSegment)}
      editable
      onAdd={() => showModal(ModalName.SegmentForm)}
      onCloseMenu={closeMenu}
    />,
    <FilterGroup
      key="guest-lists"
      label="Guest Lists"
      filters={generateFilters(GuestFilterType.EventGroup)}
      editable={canEditGuests}
      onAdd={() => showModal(ModalName.GuestListForm)}
      onCloseMenu={closeMenu}
    />,
  ]

  return (
    <>
      <Menu
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={closeMenu}
        maxH="80vh"
        menuListWidth="xs"
        placement="bottom-start"
        closeOnSelect
        autoSelect={false}
        title={selectedFilter.label}
        button={(
          <Flex align="center" fontSize="2xl" fontWeight="semibold" minW={0} data-testid="open-guest-filter-modal">
            {/* Unsaved view reuses the same neutral `circle` icon that
                marks the empty-state row "Create a filter defined by custom
                rules" inside the Custom Segments group — visually links the
                transient title back to its save target. */}
            <FilterIcon
              eventGroup={isUnsavedView ? undefined : selectedFilter.eventGroup}
              icon={isUnsavedView ? "circle" : selectedFilter.icon}
              iconColor={isUnsavedView ? "gray.600" : selectedFilter.iconColor}
              size={isUnsavedView ? 3 : 5}
              mr={isUnsavedView ? 3 : 2}
            />
            <Text isTruncated>
              {isUnsavedView ? "Unsaved Segment" : selectedFilter.label}
              {!isUnsavedView && selectedFilter.label !== "All Guests"
              && <Text as="span" fontSize="md"> ({selectedFilter.count ?? "–"})</Text>}
              {/* Dirty marker is spelled out rather than a lone asterisk so
                  the modified state reads at a glance — the Save changes
                  menu in the chip row is the other half of the signal. */}
              {isViewDirty && (
                <Text as="span" fontSize="md" fontWeight="normal" color="orange.500">
                  {" · unsaved"}
                </Text>
              )}
            </Text>
            <Icon icon="down-arrow" ml={2} flexShrink={0} />
          </Flex>
        )}
        menuItems={menuItems}
      />
      {
        editCheckInAlert
        && (
        <CheckInAlert
          teamMembers={teamMembers}
          guestList={editCheckInAlert}
          onClose={() => setEditCheckInAlert(null)}
        />
        )
      }
    </>
  )
}

const defaultFilters = (counts) => ([
  {
    type: GuestFilterType.All,
    groupId: "",
    icon: "guests",
    iconColor: "blue.500",
    label: "All Guests",
    count: counts.guests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "confirmed",
    iconColor: "blue.300",
    label: "Confirmed",
    count: counts.confirmedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "unconfirmed",
    label: "Unconfirmed",
    iconColor: "gray.400",
    count: counts.unconfirmedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "waitlisted",
    label: "Waitlisted",
    iconColor: "yellow.300",
    count: counts.waitlistedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "declined",
    iconColor: "red.300",
    label: "Declined",
    count: counts.declinedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "arrived",
    iconColor: "green.500",
    label: "Checked In",
    count: counts.arrivedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "unarrived",
    iconColor: "orange.300",
    label: "Not Checked In",
    count: counts.unarrivedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "seated",
    iconColor: "gray.600",
    label: "With Seats",
    count: counts.seatedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "unseated",
    iconColor: "gray.200",
    label: "Without Seats",
    count: counts.unseatedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "bounced",
    iconColor: "purple.300",
    label: "Bounced Emails",
    count: counts.bouncedGuests,
  },
  {
    type: GuestFilterType.DefaultSegment,
    groupId: "unassigned",
    icon: "circle",
    label: "Unassigned Registration",
    count: counts.unassignedGuests,
  },
])

export const allFilters = (
  eventGroups: EventGroup[], counts, segments: Segment[], segmentSizes: SegmentSize[] = [],
) => {
  // `icon`/`iconColor` are intentionally omitted — EditableFilter renders the
  // tag visual from `eventGroup` directly via GuestListIcon.
  // Order: API already returns event_groups sorted by `lower(title) ASC`, so
  // no client-side sort needed.
  const eventGroupFilters = eventGroups.map((g) => ({
    type: GuestFilterType.EventGroup,
    groupId: String(g.id),
    label: g.title,
    count: counts.eventGroups.find((eg) => eg.id === g.id)?.size || 0,
    eventGroup: g,
  }))

  const segmentFilters = segments.map((s) => ({
    type: GuestFilterType.CustomSegment,
    segmentId: s.id,
    icon: "dot",
    iconColor: `${s.color}.500`,
    label: s.name,
    count: segmentSizes.find((size) => size.id === s.id)?.size ?? null,
  }))

  const filters: GuestFilter[] = [
    ...defaultFilters(counts),
    ...eventGroupFilters,
    ...segmentFilters,
  ]

  return filters
}

const isSelected = (selected, current) => (
  selected.groupId === current.groupId
  && selected.segmentId === current.segmentId
)
export const findSelectedFilter = (filters, selected) => (
  filters.find((filter) => isSelected(selected, filter))
) || filters[0]

export const selectedFilter = (
  eventGroups, counts, segments, segmentId, groupId, segmentSizes?: SegmentSize[],
) => (
  findSelectedFilter(
    allFilters(eventGroups, counts, segments, segmentSizes), { segmentId, groupId },
  )
)

export const selectedFilterCount = (
  eventGroups, counts, segments, segmentId, groupId, segmentSizes?: SegmentSize[],
) => (
  selectedFilter(eventGroups, counts, segments, segmentId, groupId, segmentSizes).count
)

// Renders event and segment counts straight from the API (DIO-960, DIO-961).
export default withSectionBoundary(Filters, { name: "guest-filters" })
